import apiClient from './apiClient';
import type {
    ApiResponse,
    PaginatedResponse,
    MedicalConsultation,
    CreateConsultationRequest,
} from '../types/api';

export interface ConsultationFilters {
    page?: number;
    per_page?: number;
    doctor_id?: number;
    patient_id?: number;
    date_from?: string;
    date_to?: string;
    requires_admission?: boolean;
    search?: string;
}

export const consultationService = {
    async getAll(filters: ConsultationFilters = {}): Promise<PaginatedResponse<MedicalConsultation>> {
        const params = new URLSearchParams();
        if (filters.page) params.set('page', String(filters.page));
        params.set('per_page', String(filters.per_page || 15));
        if (filters.doctor_id) params.set('doctor_id', String(filters.doctor_id));
        if (filters.patient_id) params.set('patient_id', String(filters.patient_id));
        if (filters.date_from) params.set('date_from', filters.date_from);
        if (filters.date_to) params.set('date_to', filters.date_to);
        if (filters.requires_admission !== undefined) params.set('requires_admission', String(filters.requires_admission));
        if (filters.search) params.set('search', filters.search);
        const response = await apiClient.get<PaginatedResponse<MedicalConsultation>>(`/consultations?${params.toString()}`);
        return response.data;
    },

    /**
     * Get consultation by ID
     */
    async getById(id: number): Promise<MedicalConsultation> {
        const response = await apiClient.get<ApiResponse<MedicalConsultation>>(`/consultations/${id}`);
        return response.data.data;
    },

    /**
     * Get patient consultation history
     */
    async getPatientHistory(patientId: number): Promise<MedicalConsultation[]> {
        const response = await apiClient.get<ApiResponse<MedicalConsultation[]>>(`/consultations/patient/${patientId}/history`);
        return response.data.data;
    },

    /**
     * Get consultations requiring admission
     */
    async getRequiringAdmission(): Promise<MedicalConsultation[]> {
        const response = await apiClient.get<ApiResponse<MedicalConsultation[]>>('/consultations/requires-admission');
        return response.data.data;
    },

    /**
     * Create new consultation
     */
    async create(data: CreateConsultationRequest): Promise<MedicalConsultation> {
        const response = await apiClient.post<ApiResponse<MedicalConsultation>>('/consultations', data);
        return response.data.data;
    },

    /**
     * Update consultation
     */
    async update(id: number, data: Partial<CreateConsultationRequest>): Promise<void> {
        await apiClient.put(`/consultations/${id}`, data);
    },

    /**
     * Delete consultation
     */
    async delete(id: number): Promise<void> {
        await apiClient.delete(`/consultations/${id}`);
    },

    /**
     * Sign a draft consultation (clinical-record-signing R2-R4, R16). Sends
     * the client's last-known `lock_version`; the backend returns 409 on a
     * stale version and 422 if it is not a draft, always after resolving
     * authorization first.
     */
    async sign(id: number, lockVersion: number): Promise<MedicalConsultation> {
        const response = await apiClient.post<ApiResponse<MedicalConsultation>>(`/consultations/${id}/sign`, {
            lock_version: lockVersion,
        });
        return response.data.data;
    },

    /**
     * Download full consultation summary PDF
     */
    async downloadPdf(id: number): Promise<void> {
        const response = await apiClient.get<ArrayBuffer>(`/consultations/${id}/pdf`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([response.data as BlobPart], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `consulta_${id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    },
};

export default consultationService;
